import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactFlow, {
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  Edge,
  FitViewOptions,
  MarkerType,
  Node,
  NodeTypes,
  OnConnect,
  ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from 'reactflow';

import DiagramNode, { DiagramNodeData } from '@/component-library/primitives/DiagramNode/DiagramNode';
import NodeEditModal from '@/modules/Dashboard/Campaigns/CreateCampaign/NodeEditModal';

const nodeTypes: NodeTypes = {
  custom: DiagramNode,
};

// Updated initial nodes with improved styling
const initialNodes: Node<DiagramNodeData>[] = [
  {
    id: 'trigger-node',
    type: 'custom',
    data: {
      name: 'Trigger',
      type: 'input',
      className: 'bg-gray-800 border-l-4 border-yellow-500',
      emoji: '⚡',
      icon: 'trigger', // This will be used to determine which icon to display
    },
    position: { x: 200, y: 50 },
    deletable: false,
  },
  {
    id: 'exit-node',
    type: 'custom',
    data: {
      name: 'Exit',
      type: 'output',
      className: 'bg-gray-800 border-l-4 border-green-500',
      emoji: '🏁',
      icon: 'exit', // This will be used to determine which icon to display
    },
    position: { x: 600, y: 50 },
  },
];

const proOptions = { hideAttribution: true };

const fitViewOptions: FitViewOptions = {
  padding: 1.0,
};

let id = 0;
const getId = () => `dndnode_${id++}`;

const Diagram = () => {
  const { t } = useTranslation();
  const [showDragBlockMsg, setShowDragBlockMsg] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge[]>([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // State for the node edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node<DiagramNodeData> | null>(null);

  const onConnect: OnConnect = useCallback(
    (params: any) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            label: '+',
            labelBgPadding: [8, 4],
            labelBgBorderRadius: 4,
            labelBgStyle: { fill: '#B4F0D4', color: '#fff', fillOpacity: 1 },
            labelStyle: { fill: '#1C222A', fontSize: '15px', fontWeight: 600 },
            type: 'step',
            style: { stroke: '#B4F0D4' },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#B4F0D4' },
          },
          eds,
        ),
      ),
    [setEdges],
  );
  const onDragOver = useCallback((event: any) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Determine icon type based on node name
  const getIconType = (nodeName: string): string => {
    if (nodeName.includes('Email')) return 'email';
    if (nodeName.includes('Push')) return 'push';
    if (nodeName.includes('SMS')) return 'sms';

    if (nodeName.includes('Batch Update')) return 'batchUpdate';
    if (nodeName.includes('Send Event')) return 'sendEvent';
    if (nodeName.includes('Manual Segment')) return 'manualSegment';
    if (nodeName.includes('Query Collection')) return 'queryCollection';
    if (nodeName.includes('Create or Update Person')) return 'createPerson';

    if (nodeName.includes('Wait Until')) return 'waitUntil';
    if (nodeName.includes('Time Delay')) return 'timeDelay';
    if (nodeName.includes('Time Window')) return 'timeWindow';

    if (nodeName.includes('True/False')) return 'trueFalse';
    if (nodeName.includes('Multi-Split')) return 'multiSplit';
    if (nodeName.includes('Exit')) return 'exit';

    return 'default';
  };

  // Get border color based on node category
  const getBorderColor = (nodeColor: string): string => {
    if (nodeColor.includes('secondary-400')) return 'border-blue-500';
    if (nodeColor.includes('yellow-200')) return 'border-amber-500';
    if (nodeColor.includes('red-400')) return 'border-red-500';
    if (nodeColor.includes('primary-400')) return 'border-green-500';
    return 'border-gray-500';
  };

  const onDrop = useCallback(
    (event: any) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper?.current?.getBoundingClientRect();
      const nodeName = event.dataTransfer.getData('reactflow-nodeName');
      const nodeColor = event.dataTransfer.getData('reactflow-color');
      const nodeEmoji = event.dataTransfer.getData('reactflow-emoji');

      if (typeof nodeName === 'undefined' || !nodeName) {
        return;
      } else if (typeof nodeColor === 'undefined' || !nodeColor) {
        return;
      } else if (typeof nodeEmoji === 'undefined' || !nodeEmoji) {
        return;
      }

      if (reactFlowBounds && reactFlowInstance) {
        const position = reactFlowInstance.project({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        });

        // Determine the icon type and border color for the node
        const iconType = getIconType(nodeName);
        const borderColor = getBorderColor(nodeColor);

        const newNode: Node<DiagramNodeData> = {
          id: getId(),
          type: 'custom',
          data: {
            name: nodeName as string,
            type: 'default',
            className: `bg-gray-800 border-l-4 ${borderColor}`,
            emoji: nodeEmoji as string,
            icon: iconType,
            originalColor: nodeColor, // Store original color for reference
          },
          position: position,
        };

        setNodes((nds) => nds.concat(newNode));
        setShowDragBlockMsg(false);
      }
    },
    [reactFlowInstance],
  );

  // Handle node click to open the edit modal
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node<DiagramNodeData>) => {
    // Prevent the event from triggering other handlers
    event.stopPropagation();

    // Set the selected node and open the modal
    setSelectedNode(node);
    setIsEditModalOpen(true);
  }, []);

  // Save the updated node description
  const onSaveNodeDescription = useCallback(
    (description: string) => {
      if (!selectedNode) return;

      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === selectedNode.id) {
            // Update the node data with the new description
            return {
              ...node,
              data: {
                ...node.data,
                description,
              },
            };
          }
          return node;
        }),
      );
    },
    [selectedNode, setNodes],
  );

  return (
    <>
      <section className="relative lg:w-[50rem] lg:h-[37.5rem] 3xl:w-[66.75rem] 3xl:h-[37.5rem] px-6 py-6 shadow rounded-lg bg-custom-gray text-custom-aliceBlue">
        {showDragBlockMsg && (
          <div className="flex items-center justify-center w-[100%] h-[90%] absolute">
            <div className="flex flex-col items-center gap-2">
              <p className="text-xl">👌</p>
              <p className="text-base text-gray-300">{t('CreateCampaignPage.dragBlockLabel')}</p>
            </div>
          </div>
        )}
        <div className="reactflow-wrapper w-[100%] h-[100%]" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            nodeTypes={nodeTypes}
            className="bg-custom-gray"
            proOptions={proOptions}
            fitView
            fitViewOptions={fitViewOptions}
            connectionLineStyle={{ stroke: '#B4F0D4' }}
            deleteKeyCode={['Delete', 'Backspace']}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
          >
            <Controls className="bg-white flex flex-row" position={'top-right'} />
            <Background color="#D9D9D908" variant={BackgroundVariant.Dots} gap={6} size={3} />
          </ReactFlow>
        </div>
      </section>
      {selectedNode && (
        <NodeEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          nodeName={selectedNode.data.name}
          nodeDescription={selectedNode.data.description || ''}
          onSave={onSaveNodeDescription}
        />
      )}
    </>
  );
};

export default Diagram;
